import { z } from 'zod';
// ============================================================================
// System info (sent in registration + heartbeat)
// ============================================================================
export const SystemInfoSchema = z.object({
    os: z.string().optional(),
    arch: z.string().optional(),
    kernel: z.string().optional(),
    node_version: z.string().optional(),
    memory_mb_total: z.number().int().nonnegative().optional(),
    memory_mb_used: z.number().int().nonnegative().optional(),
    disk_gb_free: z.number().int().nonnegative().optional(),
    load_avg_1m: z.number().nonnegative().optional(),
});
// ============================================================================
// Connector entity
// ============================================================================
export const ConnectorStatusSchema = z.enum(['pending', 'active', 'stale', 'revoked', 'superseded']);
export const ConnectorSchema = z.object({
    id: z.string().regex(/^conn_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    hostname: z.string(),
    pbx_serial: z.string().nullable(),
    connector_version: z.string(),
    status: ConnectorStatusSchema,
    last_heartbeat_at: z.string().datetime().nullable(),
    last_system_info: SystemInfoSchema.nullable(),
    created_at: z.string().datetime(),
});
// ============================================================================
// Registration (one-time enrollment-token exchange)
// ============================================================================
export const RegisterRequestSchema = z.object({
    enrollment_token: z.string().min(1),
    connector_version: z.string().min(1),
    hostname: z.string().min(1),
    pbx_serial: z.string().optional(),
    system: SystemInfoSchema,
});
export const ConnectorWildixS2SConfigSchema = z.object({
    host: z.string().url().optional(),
    app_id: z.string().optional(),
    app_name: z.string().optional(),
    /** Returned only on registration / explicit refresh; not in normal config polls. */
    secret_key: z.string().optional(),
    user_extension: z.string().optional(),
});
export const ConnectorConfigSchema = z.object({
    poll_timeout_seconds: z.number().int().min(1).max(60).default(30),
    heartbeat_interval_seconds: z.number().int().min(10).max(600).default(60),
    max_concurrent_commands: z.number().int().min(1).max(50).default(5),
    wildix_s2s: ConnectorWildixS2SConfigSchema.optional(),
});
export const RegisterResponseSchema = z.object({
    connector_token: z.string(),
    connector_id: z.string().regex(/^conn_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    config: ConnectorConfigSchema,
});
// ============================================================================
// Heartbeat
// ============================================================================
export const HeartbeatSchema = z.object({
    connector_version: z.string(),
    uptime_seconds: z.number().int().nonnegative(),
    commands_processed_since_last_heartbeat: z.number().int().nonnegative().optional(),
    commands_failed_since_last_heartbeat: z.number().int().nonnegative().optional(),
    system: SystemInfoSchema,
});
// ============================================================================
// Commands (discriminated union)
// ============================================================================
/**
 * Entry-points into the on-PBX x-streamConnect dialplan. The `.call` file's
 * `Extension:` line targets one of these:
 *   - `start`     identity check first, then routes to reminder/campaign
 *   - `reminder`  reminder voice playback (skip identity check)
 *   - `campaign`  campaign voice playback
 *   - `999`       review/IVR entry
 */
export const DialplanEntryExtensionSchema = z.enum(['start', 'reminder', 'campaign', '999']);
export const PlaceCallPayloadSchema = z.object({
    type: z.literal('place_call'),
    callee_phone: z.string().min(1),
    caller_id_name: z.string().optional(),
    caller_id_number: z.string().optional(),
    dialplan_context: z.string().default('x-streamConnect'),
    dialplan_extension: z.string(),
    max_retries: z.number().int().nonnegative().default(3),
    retry_time_seconds: z.number().int().nonnegative().default(60),
    wait_time_seconds: z.number().int().nonnegative().default(0),
    /** @deprecated Use `dialplan_vars` going forward. Kept for back-compat with
     * Phase 1 connectors; the new connector treats `variables` as a fallback
     * when `dialplan_vars` is absent. */
    variables: z.record(z.string()).default({}),
    /** Emitted as `Setvar: <key>=<value>` lines in the .call file. */
    dialplan_vars: z.record(z.string()).default({}),
    /** Emitted as `Setvar: __<key>=<value>` so child channels (Local/, bridges)
     * inherit the value across channel hops. */
    dialplan_vars_inherited: z.record(z.string()).default({}),
    /** The .call file's `Extension:` line. Defaults to 'start'. */
    entry_extension: DialplanEntryExtensionSchema.default('start'),
    /** The .call file's top-level `CallerID:` line — generic, separate from the
     * outbound CID built by XXXXX. from cidname/cidnumber. */
    caller_id_text: z.string().default('Appointment Reminder'),
});
export const PlaceAnnouncementPayloadSchema = z.object({
    type: z.literal('place_announcement'),
    page_group_id: z.string().min(1),
    audio: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('tts'), voice: z.string(), text: z.string() }),
        z.object({ kind: z.literal('file'), url: z.string().url() }),
    ]),
});
export const LookupPhonebookPayloadSchema = z.object({
    type: z.literal('lookup_phonebook'),
    phonebook_id: z.string(),
    search: z.string().optional(),
    fields: z.array(z.enum(['name', 'mobile', 'office', 'home', 'email'])).optional(),
});
export const CheckDncPayloadSchema = z.object({
    type: z.literal('check_dnc'),
    phone: z.string(),
});
export const CancelPayloadSchema = z.object({
    type: z.literal('cancel'),
    target_command_id: z.string(),
});
/**
 * Phase 3b — outbound SMS via Wildix internal REST.
 *
 * The connector POSTs to `http://127.0.0.1/api/v1/SMS/SendSMS` on the PBX
 * using HTTP Basic auth. The PBX itself does the carrier handoff — no
 * Twilio etc. needed.
 *
 * Credentials (`wildix_rest_userpwd`) and the default sender number
 * (`cid_number`) are NOT carried in the payload; the connector fetches
 * them on demand from `/v1/internal/customer-config/sms-credentials`
 * (connector-auth gated, customer-scoped, short-TTL cached on the
 * connector side). This avoids persisting the PBX REST password in the
 * `commands` table.
 */
export const SendSmsPayloadSchema = z.object({
    type: z.literal('send_sms'),
    to_phone: z.string().min(1),
    message: z.string().min(1),
});
/** Response shape of `/v1/internal/customer-config/sms-credentials`. */
export const SmsCredentialsResponseSchema = z.object({
    /** HTTP Basic credential `user:password` for the PBX SMS API. */
    wildix_rest_userpwd: z.string().min(1),
    /** Optional default sender number (E.164). */
    from_phone: z.string().nullable(),
});
export const CommandPayloadSchema = z.discriminatedUnion('type', [
    PlaceCallPayloadSchema,
    PlaceAnnouncementPayloadSchema,
    LookupPhonebookPayloadSchema,
    CheckDncPayloadSchema,
    CancelPayloadSchema,
    SendSmsPayloadSchema,
]);
export const CommandSchema = z.object({
    id: z.string().regex(/^cmd_[A-Za-z0-9_-]+$/),
    type: z.enum([
        'place_call',
        'place_announcement',
        'lookup_phonebook',
        'check_dnc',
        'cancel',
        'send_sms',
    ]),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    idempotency_key: z.string().optional(),
    deadline: z.string().datetime().optional(),
    created_at: z.string().datetime(),
    payload: CommandPayloadSchema,
});
export const CommandBatchSchema = z.object({
    commands: z.array(CommandSchema),
});
// ============================================================================
// Status updates from connector
// ============================================================================
export const CommandStatusSchema = z.enum(['in_progress', 'completed', 'failed']);
export const CommandStatusUpdateSchema = z.object({
    status: CommandStatusSchema,
    result: z.record(z.unknown()).optional(),
    error: z
        .object({
        code: z.string(),
        message: z.string(),
        retryable: z.boolean().optional(),
    })
        .optional(),
    observed_at: z.string().datetime().optional(),
});
// ============================================================================
// Connector JWT payload (separate from access tokens)
// ============================================================================
export const ConnectorTokenPayloadSchema = z.object({
    sub: z.string().regex(/^conn_[A-Za-z0-9_-]+$/),
    iss: z.string().url(),
    aud: z.union([z.string(), z.array(z.string())]),
    iat: z.number().int(),
    exp: z.number().int(),
    jti: z.string(),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    connector_id: z.string().regex(/^conn_[A-Za-z0-9_-]+$/),
    token_type: z.literal('connector'),
});
//# sourceMappingURL=connector.js.map